import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { 
  Landmark, RefreshCw, Download, Filter, 
  ArrowRight, Search, Clock, CheckCircle2, AlertTriangle, Loader2 
} from "lucide-react";
import Pagination from "@/components/Pagination";

export default function Settlements() {
  const [data, setData] = useState({ items: [], total: 0, today_pending_paise: 0, today_settled_paise: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "", from_date: "", to_date: "" });
  
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      params.page = page;
      params.limit = PAGE_SIZE;
      const res = await api.get("/admin/transfers", { params });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load settlements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (page === 1) load(); else setPage(1);
  };

  const fmt = (paise) => `₹${(Number(paise || 0) / 100).toFixed(2)}`;
  
  const handleExport = () => {
    if (!data.items.length) return;
    const h = "Date,Recharge ID,Payment Gateway Ref,Total Collected,Platform Fee,Admin Bank Settlement,Status\n";
    const b = data.items.map(r => [
      r.created_at?.slice(0, 19).replace('T', ' '),
      r.recharge_id,
      r.razorpay_payment_id || '',
      (r.total_amount / 100).toFixed(2),
      (r.platform_commission / 100).toFixed(2),
      (r.admin_amount / 100).toFixed(2),
      r.status
    ].join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([h + b], { type: 'text/csv' }));
    a.download = `settlements_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const statusTone = (s) => {
    if (s === 'processed') return "bg-green-50 text-green-700 border-green-200";
    if (s === 'created' || s === 'pending') return "bg-blue-50 text-blue-700 border-blue-200";
    if (s === 'failed' || s === 'error') return "bg-red-50 text-red-700 border-red-200";
    return "bg-slate-100 text-slate-600 border-slate-200";
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav flex items-center gap-2">
            <Landmark className="w-6 h-6 text-brand-primary" /> Settlement Report
          </h3>
          <p className="text-slate-500 text-sm mt-1">
            Track exactly how much money hits your connected bank account after Razorpay splits and fees.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} disabled={!data.items.length} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover px-3 py-2 rounded-lg transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Today's Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 rounded-xl p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-4 right-4 opacity-20"><Clock className="w-16 h-16 text-blue-600" /></div>
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-1">Expected Today</p>
          <p className="font-heading font-black text-3xl text-blue-900">{fmt(data.today_pending_paise)}</p>
          <p className="text-xs text-blue-600/80 mt-1 font-medium">Customer paid, currently processing by Razorpay.</p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 shadow-sm relative overflow-hidden">
          <div className="absolute top-4 right-4 opacity-20"><CheckCircle2 className="w-16 h-16 text-green-600" /></div>
          <p className="text-xs font-bold uppercase tracking-widest text-green-600 mb-1">Settled Today</p>
          <p className="font-heading font-black text-3xl text-green-900">{fmt(data.today_settled_paise)}</p>
          <p className="text-xs text-green-600/80 mt-1 font-medium">Cleared to your connected bank account.</p>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="mb-4 bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-bold uppercase text-slate-400 mb-1.5">Status</label>
          <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} className="w-36 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/50">
            <option value="">All Status</option>
            <option value="processed">Settled (Processed)</option>
            <option value="created">Pending</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase text-slate-400 mb-1.5">From Date</label>
          <input type="date" value={filters.from_date} onChange={e => setFilters(p => ({ ...p, from_date: e.target.value }))} className="w-40 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/50" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase text-slate-400 mb-1.5">To Date</label>
          <input type="date" value={filters.to_date} onChange={e => setFilters(p => ({ ...p, to_date: e.target.value }))} className="w-40 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/50" />
        </div>
        <button type="submit" className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors">
          Filter
        </button>
      </form>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="px-5 py-3 text-left">Date</th>
              <th className="px-5 py-3 text-left">Transaction ID</th>
              <th className="px-5 py-3 text-right">Customer Paid</th>
              <th className="px-5 py-3 text-right">Platform Fee</th>
              <th className="px-5 py-3 text-right">Bank Settlement</th>
              <th className="px-5 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && data.items.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading...</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400"><Landmark className="w-10 h-10 mx-auto mb-3 opacity-30" />No settlements found for this period.</td></tr>
            ) : (
              data.items.map(r => (
                <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-5 py-4 text-xs font-mono text-slate-500">{r.created_at?.slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-5 py-4">
                    <p className="font-mono text-slate-700 font-medium">{r.razorpay_payment_id || "N/A"}</p>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">Order: {r.recharge_id.slice(0,8)}</p>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <span className="font-semibold text-slate-700">{fmt(r.total_amount)}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <span className="text-amber-600 font-medium">{fmt(r.platform_commission)}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <span className="font-bold text-green-600 bg-green-50 px-2 py-1 rounded">{fmt(r.admin_amount)}</span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusTone(r.status)}`}>
                      {r.status === 'processed' ? 'Settled' : r.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {pageCount > 1 && (
          <div className="p-4 border-t border-slate-100">
            <Pagination page={page} pageCount={pageCount} onChange={setPage} total={data.total} />
          </div>
        )}
      </div>
    </div>
  );
}
